'use client';

import { useState, type ReactElement } from 'react';
import { TbFileText } from 'react-icons/tb';

import { getReportHtml } from './actions';
import type { ReportBlockPayload } from './types';

import { ReportViewerDialog } from '@/app/(app)/relatorios/_components/report-viewer-dialog';

function brMoney(v: unknown): string {
  const n = typeof v === 'number' ? v : Number(v ?? 0);
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
}

export function ReportBlock({ payload }: { payload: ReportBlockPayload }): ReactElement {
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!payload.has_snapshot) {
    return (
      <div className="border-border-subtle/60 bg-surface-elevated/40 rounded-2xl border p-4">
        <p className="text-ink text-sm font-semibold">Ainda nao ha relatorio gerado</p>
        <p className="text-ink-muted mt-1 text-xs">
          Assim que houver dados, seu relatorio aparece aqui.
        </p>
        <a
          href="/relatorios"
          className="bg-trakd-fill text-on-brand mt-3 inline-block rounded-lg px-3 py-2 text-xs font-semibold"
        >
          Ir pra Relatorios
        </a>
      </div>
    );
  }

  const s = payload.summary ?? {};
  async function load(): Promise<void> {
    if (html || loading) return;
    setLoading(true);
    const r = await getReportHtml();
    setHtml(r.html);
    setLoading(false);
  }

  return (
    <div className="border-border-subtle/60 bg-surface-elevated/40 rounded-2xl border p-4">
      <div className="flex items-center gap-3">
        <span className="bg-trakd-fill text-on-brand flex h-10 w-10 items-center justify-center rounded-lg">
          <TbFileText size={20} />
        </span>
        <div>
          <p className="text-ink text-sm font-bold">Relatorio Trakd</p>
          <p className="text-ink-muted text-[11px]">
            {payload.period_from} a {payload.period_to}
          </p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <div className="bg-surface-elevated/60 rounded-lg p-2">
          <p className="text-ink text-sm font-bold">{brMoney(s['faturamento'])}</p>
          <p className="text-ink-muted text-[9px] uppercase">Faturamento</p>
        </div>
        <div className="bg-surface-elevated/60 rounded-lg p-2">
          <p className="text-ink text-sm font-bold">{String(s['vendasCount'] ?? 0)}</p>
          <p className="text-ink-muted text-[9px] uppercase">Vendas</p>
        </div>
        <div className="bg-surface-elevated/60 rounded-lg p-2">
          <p className="text-ink text-sm font-bold">
            {s['roas'] != null ? `${Number(s['roas']).toFixed(1)}x` : '-'}
          </p>
          <p className="text-ink-muted text-[9px] uppercase">ROAS</p>
        </div>
      </div>
      <div className="mt-3" onMouseEnter={() => void load()}>
        {html ? (
          <ReportViewerDialog
            html={html}
            periodFrom={payload.period_from ?? null}
            periodTo={payload.period_to ?? null}
            generatedAt={payload.generated_at ?? null}
            trigger={
              <button
                type="button"
                className="bg-trakd-fill text-on-brand w-full rounded-lg py-2 text-xs font-semibold"
              >
                Abrir relatorio
              </button>
            }
          />
        ) : (
          <button
            type="button"
            className="bg-trakd-fill text-on-brand w-full rounded-lg py-2 text-xs font-semibold"
            onClick={() => void load()}
          >
            {loading ? 'Carregando...' : 'Abrir relatorio'}
          </button>
        )}
      </div>
    </div>
  );
}
